import {
  ApplicationConfig,
  LOCALE_ID,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { provideRouter, withRouterConfig } from '@angular/router';
import { registerLocaleData } from '@angular/common';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import localeEsMX from '@angular/common/locales/es-MX';
import { es_ES, provideNzI18n } from 'ng-zorro-antd/i18n';
import { firstValueFrom } from 'rxjs';

import { routes } from './app.routes';
import { credentialsInterceptor } from './interceptors/credentials.interceptor';
import { AuthHttpService, AuthService, AuthSimuladoService, sesionExpiradaInterceptor } from '@auth';
import { environment } from 'src/environments/environment';

registerLocaleData(localeEsMX);

/**
 * En produccion NO se cae al simulado: se LANZA.
 *
 * Un build productivo que aceptara `demo/demo` es un incidente, no un bug — y en
 * el login CENTRAL de la plataforma seria la puerta de todos los sistemas a la
 * vez. Esto es la segunda red; la primera es R5 de `pnpm run audit`, que corre
 * en CI. Existe porque el `useClass` de abajo no falla por si solo: proveeria el
 * simulado tan campante.
 */
if (environment.production && environment.authSimulado) {
  throw new Error(
    'authSimulado esta activo en un build de produccion. Revisa environments/environment.prod.ts.',
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      // El `volverA` tiene que sobrevivir a la redireccion del `**` al login:
      // sin esto, quien llega a una ruta desconocida con el parametro puesto lo
      // pierde y acaba en la rejilla en vez de en su destino.
      withRouterConfig({ paramsInheritanceStrategy: 'always' }),
    ),
    provideBrowserGlobalErrorListeners(),
    // ⚠️ El orden importa: `credentials` marca la peticion ANTES de que
    // `sesionExpirada` pueda reintentarla tras refrescar, asi que el reintento
    // conserva `withCredentials` y el navegador adjunta la cookie NUEVA.
    //
    // Y `credentialsInterceptor` no es opcional en ningun caso: sin el, el
    // navegador no adjunta la cookie y TODO responde 401 aunque la sesion este
    // perfectamente abierta en el servidor. No hay nada que lo delate.
    provideHttpClient(withInterceptors([credentialsInterceptor, sesionExpiradaInterceptor])),
    provideAnimationsAsync(),
    provideNzI18n(es_ES),
    // `registerLocaleData` NO alcanza: sin este provider el LOCALE_ID sigue en
    // `en-US` y los pipes se van con él. Falla sin error.
    { provide: LOCALE_ID, useValue: 'es-MX' },
    // `AuthService` es una clase ABSTRACTA, no una interfaz: una interfaz
    // desaparece al compilar y no sirve como token de inyeccion.
    {
      provide: AuthService,
      useClass: environment.authSimulado ? AuthSimuladoService : AuthHttpService,
    },
    // Rehidrata la sesion ANTES del primer render. Sin esto, un F5 en la rejilla
    // pinta el formulario de credenciales por un instante y el parpadeo se lee
    // como un cierre de sesion espurio.
    provideAppInitializer(() => firstValueFrom(inject(AuthService).restaurarSesion())),
  ],
};
